// Model-version-keyed (source-keyed) memo over `buildErrorRecoveryView`.
//
// PARSE CENTRALIZATION (postmerge-C resolution 2): mysql / oracle / sqlite
// already tree-sitter-parse the live buffer on a 350 ms debounce via the VQE
// compact hook `useLocalSyntaxErrorEntries`. The new live-marker pass
// (`collectTreeSitterSyntaxMarkers`) needs the SAME recovery view. Rather than
// add an independent second `parser.parse` of the same model, both consumers
// route through this cache: the first caller for a given `(engineId, source)`
// runs the parse, the second reuses the in-flight (or settled) promise. On an
// idle buffer both debounced passes land on the identical final source → ONE
// parse. During typing each intermediate source is distinct, cheap, and
// debounced. Only the last result per engine is retained (bounded memory; the
// buffer is size-capped upstream at MAX_SOURCE_LEN).
//
// The source string is a stronger equality than a model version id (identical
// text ⇒ identical parse), so keying on it needs no Monaco handle here and
// stays pure `(engineId, source) → Promise<view>`.

import type { DatabaseEngineId } from '@/core/engines';

import { buildErrorRecoveryView } from './errorRecoveryViewBuilder';
import type { ErrorRecoveryEntry } from './suppressor';

type CacheEntry = {
  readonly source: string;
  readonly promise: Promise<ReadonlyArray<ErrorRecoveryEntry> | null>;
};

const lastByEngine = new Map<DatabaseEngineId, CacheEntry>();

/**
 * Shared, deduplicated `buildErrorRecoveryView` for the two live tree-sitter
 * consumers. Returns the cached promise when `source` matches the last request
 * for `engineId`; otherwise parses and caches. Same contract as
 * `buildErrorRecoveryView` (`null` = no grammar fork / parse threw).
 */
export function getSharedErrorRecoveryView(
  engineId: DatabaseEngineId,
  source: string,
): Promise<ReadonlyArray<ErrorRecoveryEntry> | null> {
  const existing = lastByEngine.get(engineId);
  if (existing && existing.source === source) return existing.promise;
  const promise = buildErrorRecoveryView(engineId, source);
  lastByEngine.set(engineId, { source, promise });
  return promise;
}

/** Test-only: drop memoized results so a WASM-loader swap can't be masked. */
export function __resetSharedRecoveryViewForTest(): void {
  lastByEngine.clear();
}
